import { useEffect, useState } from 'react';
import { callEndpoint, telemetry } from '../../../../services/backend';

interface WeatherReferenceLocation {
  name: string;
  description?: string;
  latitude: number;
  longitude: number;
}

interface WeatherSummaryLocationResponse {
  weatherLocation?: {
    name?: string;
    region?: string;
    state?: string;
    country?: string;
    latitude?: number;
    longitude?: number;
  };
}

const formatRegionAndCountry = (
  region?: string,
  state?: string,
  country?: string,
) => {
  let description = region || '';

  if (region && (state || country)) {
    description += ',';
  }

  if (state) {
    description += ` ${state}`;
  }

  if (country) {
    description += ` ${country}`;
  }

  return description.trim() || undefined;
};

const hasValidCoordinates = (
  latitude: number | undefined,
  longitude: number | undefined,
) =>
  latitude != null &&
  longitude != null &&
  Number.isFinite(latitude) &&
  Number.isFinite(longitude);

export const useWeatherReferenceLocation = (
  latitude: number | undefined,
  longitude: number | undefined,
) => {
  const [location, setLocation] = useState<WeatherReferenceLocation | null>(
    null,
  );
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);

  useEffect(() => {
    let isMounted = true;

    if (!hasValidCoordinates(latitude, longitude)) {
      setLocation(null);
      setLoadError(false);
      setIsLoading(true);
      return () => {
        isMounted = false;
      };
    }

    const loadLocation = async () => {
      setIsLoading(true);
      setLoadError(false);
      setLocation(null);

      try {
        const data = (await callEndpoint(
          callEndpoint.endpoints.queryWeatherSummary,
          {},
          { latitude, longitude },
        )) as WeatherSummaryLocationResponse;

        if (!isMounted) {
          return;
        }

        const weatherLocation = data?.weatherLocation;

        if (!weatherLocation?.name || weatherLocation.latitude == null) {
          setLoadError(true);
          return;
        }

        setLocation({
          name: weatherLocation.name,
          description: formatRegionAndCountry(
            weatherLocation.region,
            weatherLocation.state,
            weatherLocation.country,
          ),
          latitude: weatherLocation.latitude,
          longitude: weatherLocation.longitude ?? (longitude as number),
        });
      } catch (error) {
        telemetry(
          telemetry.ERROR,
          `[useWeatherReferenceLocation.loadLocation] ${JSON.stringify(error)}`,
        );

        if (isMounted) {
          setLoadError(true);
        }
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };

    loadLocation();

    return () => {
      isMounted = false;
    };
  }, [latitude, longitude]);

  return {
    location,
    isLoading,
    loadError,
  };
};
